'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Terminal as XTerm } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import { SearchAddon } from '@xterm/addon-search';
import { Unicode11Addon } from '@xterm/addon-unicode11';
import '@xterm/xterm/css/xterm.css';
import { Box } from '@terra/ui-styled-system';
import { useThemeMode } from '@terra/ui-design-system';
import { ensureSessionTerminal } from '../hooks/useTerminalExecution';
import { useTerminalStore, type ShellType } from '../store/terminalStore';
import { getCurrentTenantId } from '../store/tenantStorage';
import { getTerminalShortcutSequence } from './terminal-shortcuts';
import {
  fitTerminalToContainer,
  waitForStableContainerSize,
} from './terminal-layout';
import { TERMINAL_FONT_FAMILY, getTerminalTheme } from './terminal-theme';
import { connectAgentPtyTerminal } from './agent-pty-terminal-connection';

const PTY_RESIZE_DEBOUNCE_MS = 50;

type TerminalInstanceProps = {
  sessionId?: string;
  ptyId?: string;
  cwd?: string;
  shell?: ShellType;
  isVisible?: boolean;
  onSessionCreated?: (terminalId: string) => void;
  onExit?: (exitCode: number) => void;
};

export function TerminalInstance({
  sessionId,
  ptyId,
  cwd,
  shell,
  isVisible = true,
  onSessionCreated,
  onExit,
}: TerminalInstanceProps) {
  const terminalRef = useRef<HTMLDivElement>(null);
  const xtermRef = useRef<XTerm | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const terminalIdRef = useRef<string | null>(null);
  const { mode: themeMode } = useThemeMode();
  const [xterm, setXterm] = useState<XTerm | null>(null);
  const [isOpened, setIsOpened] = useState(false);
  const [isConnected, setIsConnected] = useState(false);

  const onSessionCreatedRef = useRef(onSessionCreated);
  const onExitRef = useRef(onExit);

  onSessionCreatedRef.current = onSessionCreated;
  onExitRef.current = onExit;

  const setTerminalReady = useTerminalStore((state) => state.setTerminalReady);
  const focusRequestId = useTerminalStore((state) => state.focusRequestId);
  const defaultShell = useTerminalStore((state) => state.defaultShell);
  const scopeKey = getCurrentTenantId() || 'global';

  const defaultShellRef = useRef(defaultShell);

  defaultShellRef.current = defaultShell;
  const scopeKeyRef = useRef(scopeKey);

  scopeKeyRef.current = scopeKey;

  const terminalTheme = useMemo(
    () => getTerminalTheme({ isDark: themeMode === 'dark' }),
    [themeMode],
  );
  const terminalThemeRef = useRef(terminalTheme);

  terminalThemeRef.current = terminalTheme;

  useEffect(() => {
    if (typeof window === 'undefined' || !window.electronAPI?.terminal) {
      console.warn('Terminal API not available - not running in Electron');

      return;
    }

    const terminal = new XTerm({
      cursorBlink: true,
      fontSize: 12,
      fontFamily: TERMINAL_FONT_FAMILY,
      fontWeight: '400',
      fontWeightBold: '700',
      letterSpacing: 0,
      lineHeight: 1.1,
      theme: terminalThemeRef.current,
      allowTransparency: false,
      allowProposedApi: true,
      customGlyphs: true,
      scrollback: 10000,
    });

    const fitAddon = new FitAddon();

    terminal.loadAddon(fitAddon);
    terminal.loadAddon(new WebLinksAddon());
    terminal.loadAddon(new SearchAddon());
    terminal.loadAddon(new Unicode11Addon());
    terminal.unicode.activeVersion = '11';

    xtermRef.current = terminal;
    fitAddonRef.current = fitAddon;
    setXterm(terminal);

    return () => {
      terminal.dispose();
      xtermRef.current = null;
      fitAddonRef.current = null;
      setIsOpened(false);
    };
  }, []);

  useEffect(() => {
    if (!xtermRef.current) {
      return;
    }

    xtermRef.current.options.theme = terminalTheme;
  }, [terminalTheme]);

  const fitNow = useCallback((): boolean => {
    const element = terminalRef.current;
    const terminal = xtermRef.current;
    const fitAddon = fitAddonRef.current;

    if (!element || !terminal || !fitAddon) {
      return false;
    }

    try {
      const fitted = fitTerminalToContainer({
        element,
        fit: () => fitAddon.fit(),
        current: () => ({ cols: terminal.cols, rows: terminal.rows }),
      });

      return fitted !== null;
    } catch (error) {
      console.warn('[Terminal] Failed to fit terminal', error);

      return false;
    }
  }, []);

  useEffect(() => {
    const element = terminalRef.current;

    if (!xterm || !element) return;

    const abort = new AbortController();
    let cleanup: (() => void) | undefined;

    const openTerminal = async (): Promise<boolean> => {
      if (xterm.element) {
        fitNow();
        setIsOpened(true);

        return true;
      }

      await document.fonts.ready;
      if (abort.signal.aborted) return false;

      const size = await waitForStableContainerSize({
        element,
        signal: abort.signal,
      });

      if (!size || abort.signal.aborted) return false;

      xterm.open(element);
      fitNow();
      setIsOpened(true);

      return true;
    };

    const connectTerminal = async () => {
      let id: string;
      let isReconnect = false;

      if (ptyId) {
        cleanup = await connectAgentPtyTerminal({
          xterm,
          ptyId,
          signal: abort.signal,
        });
        if (!abort.signal.aborted) {
          setIsConnected(true);
        }

        return;
      }

      if (sessionId) {
        const result = await ensureSessionTerminal({
          cwd,
          sessionId,
          shell: shell || defaultShellRef.current,
          scopeKey: scopeKeyRef.current,
          defaultShell: defaultShellRef.current,
          cols: xterm.cols,
          rows: xterm.rows,
        });

        if (abort.signal.aborted) return;

        id = result.terminalId;
        isReconnect = !result.isNewSession;
      } else {
        const result = await window.electronAPI.terminal.create({
          cwd,
          cols: xterm.cols,
          rows: xterm.rows,
        });

        if (abort.signal.aborted) {
          window.electronAPI.terminal.kill(result.terminalId);

          return;
        }

        id = result.terminalId;
        isReconnect = result.isReconnect;
      }

      terminalIdRef.current = id;

      if (isReconnect) {
        window.electronAPI.terminal.resize(id, xterm.cols, xterm.rows);
        const state = await window.electronAPI.terminal.getState(id);

        if (abort.signal.aborted) return;
        if (state?.outputBuffer) {
          xterm.write(state.outputBuffer, () => xterm.scrollToBottom());
        }
      } else {
        xterm.write('\x1bc');
      }

      const unsubscribeData = window.electronAPI.terminal.onData(
        id,
        (data: string) => {
          xterm.write(data);
        },
      );

      const unsubscribeExit = window.electronAPI.terminal.onExit(
        id,
        ({ exitCode }: { exitCode: number; signal?: number }) => {
          xterm.write('\r\n\x1b[31mTerminal process exited\x1b[0m\r\n');
          setIsConnected(false);
          onExitRef.current?.(exitCode);
        },
      );

      const disposeOnData = xterm.onData((data: string) => {
        window.electronAPI.terminal.write(id, data);
      });

      xterm.attachCustomKeyEventHandler((event) => {
        if (event.type !== 'keydown' || !terminalIdRef.current) {
          return true;
        }

        const shortcutSequence = getTerminalShortcutSequence(event);

        if (shortcutSequence) {
          window.electronAPI.terminal.write(
            terminalIdRef.current,
            shortcutSequence,
          );

          return false;
        }

        return true;
      });

      cleanup = () => {
        unsubscribeData();
        unsubscribeExit();
        disposeOnData.dispose();
      };

      setIsConnected(true);
      onSessionCreatedRef.current?.(id);
    };

    void (async () => {
      try {
        if (await openTerminal()) {
          await connectTerminal();
        }
      } catch (error) {
        console.error('Failed to create terminal:', error);
        xterm.write('\r\n\x1b[31mFailed to create terminal session\x1b[0m\r\n');
      }
    })();

    return () => {
      abort.abort();
      cleanup?.();
      setIsConnected(false);
      if (sessionId) {
        setTerminalReady(sessionId, false);
      }

      if (terminalIdRef.current && window.electronAPI?.terminal) {
        window.electronAPI.terminal.disconnect(terminalIdRef.current);
        terminalIdRef.current = null;
      }
    };
  }, [cwd, fitNow, ptyId, sessionId, setTerminalReady, shell, xterm]);

  useEffect(() => {
    const element = terminalRef.current;

    if (!xterm || !isOpened || !element) return;

    let frameId: number | null = null;
    let resizeTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const syncPtySize = () => {
      if (resizeTimeoutId) {
        clearTimeout(resizeTimeoutId);
      }

      resizeTimeoutId = setTimeout(() => {
        resizeTimeoutId = null;
        if (ptyId) {
          window.electronAPI.pty
            .resize({ ptyId, cols: xterm.cols, rows: xterm.rows })
            .catch((error: unknown) => {
              console.warn('[Terminal] Agent session resize skipped', error);
            });
        } else if (terminalIdRef.current && window.electronAPI?.terminal) {
          window.electronAPI.terminal.resize(
            terminalIdRef.current,
            xterm.cols,
            xterm.rows,
          );
        }
      }, PTY_RESIZE_DEBOUNCE_MS);
    };

    const refit = () => {
      frameId = null;
      const previousCols = xterm.cols;
      const previousRows = xterm.rows;

      if (!fitNow()) {
        return;
      }

      if (xterm.cols !== previousCols || xterm.rows !== previousRows) {
        syncPtySize();
      }

      xterm.refresh(0, xterm.rows - 1);

      if (sessionId && isVisible && isConnected) {
        setTerminalReady(sessionId, true);
      }
    };

    const scheduleRefit = () => {
      if (frameId === null) {
        frameId = requestAnimationFrame(refit);
      }
    };

    const resizeObserver = new ResizeObserver(scheduleRefit);

    resizeObserver.observe(element);

    if (isVisible) {
      scheduleRefit();
    } else if (sessionId) {
      setTerminalReady(sessionId, false);
    }

    return () => {
      resizeObserver.disconnect();
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }

      if (resizeTimeoutId) {
        clearTimeout(resizeTimeoutId);
      }
    };
  }, [
    fitNow,
    isConnected,
    isOpened,
    isVisible,
    ptyId,
    sessionId,
    setTerminalReady,
    xterm,
  ]);

  useEffect(() => {
    if (!isVisible || !isConnected || !xterm || focusRequestId === 0) {
      return;
    }

    const frameId = requestAnimationFrame(() => {
      xterm.focus();
    });

    return () => cancelAnimationFrame(frameId);
  }, [focusRequestId, isConnected, isVisible, xterm]);

  return (
    <Box
      position="relative"
      h="full"
      w="full"
      padding="8px"
      overflow="hidden"
      bg="bg.canvas"
      visibility={isVisible ? 'visible' : 'hidden'}
      zIndex={isVisible ? 1 : 0}
      css={{
        '& .xterm-viewport': {
          backgroundColor: 'transparent !important',
        },
      }}
    >
      <div ref={terminalRef} style={{ height: '100%', width: '100%' }} />
    </Box>
  );
}

export const Terminal = TerminalInstance;

export function useTerminalClear(terminalId: string | null) {
  return useCallback(() => {
    if (terminalId && window.electronAPI?.terminal) {
      window.electronAPI.terminal.clear(terminalId);
    }
  }, [terminalId]);
}
